import { FixedLazyImageProps, LazyImageProps, ResponsiveLazyImageProps } from "./WebpLazyImage.types";

const imageBreakpoints = [
    80,
    160,
    320,
    480,
    768,
    1280,
];

export const isResponsiveProps = (props: LazyImageProps): props is ResponsiveLazyImageProps => {
    return 'src' in props && 'originalWidth' in props && 'sizes' in props;
};

export const isFixedProps = (props: LazyImageProps): props is FixedLazyImageProps => {
    return 'src' in props && 'originalWidth' in props && 'width' in props;
};

const shouldModify = (url: string) => {
    const isProd = import.meta.env.PROD;
    const isAssetsPath = /\/\/blackjack\.fun\//.test(url) || /^\/assets\//.test(url);
    const isCDNpath = /\/\/cdn\.blackjack\.fun\//.test(url);

    return isProd && (isAssetsPath || isCDNpath);
}

const getImageBreakpoints = (originalWidth: number) => {
    // Filter out breakpoints larger than or equal to originalWidth and add originalWidth
    const filteredBreakpoints = imageBreakpoints.filter(width => width < originalWidth);
    filteredBreakpoints.push(originalWidth);
    
    filteredBreakpoints.sort((a, b) => a - b);
    return filteredBreakpoints;
}

export const assetUrl = (url: string | undefined) => {
    if(!url || !shouldModify(url)) {
        return url;
    }

    const isAssetsPath = /\/\/blackjack\.fun\//.test(url) || /^\/assets\//.test(url);
    const isCDNpath = /\/\/cdn\.blackjack\.fun\//.test(url);
    const isWebp = /\.webp$/.test(url);

    let preparedUrl = url;

    if(!isWebp) {
        if(isCDNpath) {
            preparedUrl = preparedUrl.replace('//cdn.blackjack.fun/', '//cdn.blackjack.fun/optimized/');
        }

        if(isAssetsPath || isCDNpath) {
            preparedUrl = preparedUrl.replace(/\.(jpg|jpeg|png)$/i, '.webp');
        }
    }

    return preparedUrl;
}

const closestWidth = (originalWidth: number, neededWidth: number) => {   
    const breakpoints = getImageBreakpoints(originalWidth);

    for(let i = 0; i < breakpoints.length; i++) {
        const width = breakpoints[i];
        if (neededWidth <= width) {
            return width;
        }
    }

    return undefined;
}

const closestWidthUrl = (src: string, originalWidth: number, neededWidth: number) => {
    const width = closestWidth(originalWidth, neededWidth);
    return width && width !== originalWidth ? src.replace(/\.(jpg|jpeg|png|webp)$/i, `-${width}.$1`) : src;
}

export const assetResponsiveSrcSet = ({
    url,
    originalWidth,
    minWidth = 320,
    width,
}: {
    url: string;
    originalWidth: number;
    minWidth?: number;
    width?: number;
}) => {
    // Generate srcSet from minWidth to toWidth using imageBreakpoints
    if(!url || !shouldModify(url)) {
        return undefined;
    }

    const breakpoints = getImageBreakpoints(originalWidth);
    const srcSet: string[] = [];
    for(let i = 0; i < breakpoints.length; i++) {
        const w = breakpoints[i];
        if((minWidth && w < minWidth) || (width && w > width)) {
            continue;
        }

        srcSet.push(`${assetUrl(closestWidthUrl(url, originalWidth, w))} ${w}w`);
    }
    return srcSet.join(', ');
}

export const assetFixedSrcSet = ({
    url,
    originalWidth,
    width,
}: {
    url: string;
    originalWidth: number;
    width: number;
}) => {
    // Generate srcSet with 1x, 2x urls
    if(!url || !shouldModify(url)) {
        return undefined;
    }
    return `${assetUrl(closestWidthUrl(url, originalWidth, width))} 1x, ${assetUrl(closestWidthUrl(url, originalWidth, width * 2))} 2x`;
}

export const assetSrcSet = (props: LazyImageProps) => {
    if(isFixedProps(props)) {
        return assetFixedSrcSet({
            url: props.src,
            originalWidth: props.originalWidth,
            width: props.width,
        });
    } else if(isResponsiveProps(props)) {
        return assetResponsiveSrcSet({
            url: props.src,
            originalWidth: props.originalWidth,
            minWidth: props.minWidth,
        });
    } else {
        return undefined;
    }
}